import React from 'react';
import logo from 'images/odin-logo.svg';
import { cn, getCurrentDomain } from 'utils';
import { useScaleOnResize } from 'utils/useScaleOnResize';
import QRCode from 'react-qr-code';

export type VerticalBadgeColorScheme = 'red' | 'purple';

export type VerticalBadgeType = 'escorted' | 'temporary' | 'visitor';

const schemes: Record<
  VerticalBadgeColorScheme,
  {
    letterBackground: string;
    letterBorder: string;
    letterColor: string;
    returnPromptColor: string;
  }
> = {
  red: {
    letterBackground: cn('odin-bg-red-50'),
    letterBorder: cn('odin-border-odin-danger'),
    letterColor: cn('odin-text-odin-danger'),
    returnPromptColor: cn('odin-bg-odin-danger'),
  },
  purple: {
    letterBackground: cn('odin-bg-purple-50'),
    letterBorder: cn('odin-border-odin-primary'),
    letterColor: cn('odin-text-odin-primary'),
    returnPromptColor: cn('odin-bg-odin-primary'),
  },
};

const badgeTypeCopy: Record<
  VerticalBadgeType,
  {
    badgeTypeLetter: string;
    badgeTypeName: string;
  }
> = {
  escorted: {
    badgeTypeLetter: 'E',
    badgeTypeName: 'Escorted',
  },
  temporary: {
    badgeTypeLetter: 'T',
    badgeTypeName: 'Temporary',
  },
  visitor: {
    badgeTypeLetter: 'V',
    badgeTypeName: 'Visitor',
  },
};

export type VerticalTempBadgePreviewProps = {
  jobsiteName: string;
  badgeName: string;
  scheme: VerticalBadgeColorScheme;
  badgeType: VerticalBadgeType;
  className?: string;
};

export function VerticalTempBadgePreview(
  props: VerticalTempBadgePreviewProps,
): React.ReactElement {
  const { jobsiteName, badgeName, className, scheme, badgeType } = props;
  const { containerRef, containerStyle, subContainerStyle } =
    useScaleOnResize<HTMLDivElement>({ baseWidth: 248, baseHeight: 420 });

  const colorScheme = schemes[scheme];
  const copyScheme = badgeTypeCopy[badgeType];

  return (
    <div ref={containerRef} className={cn(className)} style={containerStyle}>
      <div
        className={cn(
          'odin-bg-white odin-border odin-border-gray-200 odin-rounded-2xl',
          'odin-shadow-odin-normal odin-overflow-hidden',
        )}
        style={subContainerStyle}
      >
        <div className="odin-w-full odin-h-full odin-flex odin-flex-col odin-items-center odin-justify-between odin-px-6 odin-pt-5 odin-pb-5 odin-gap-4">
          <img src={logo} alt="Odin Logo" className="odin-h-5 odin-w-auto" />

          <div
            className={cn(
              'odin-w-full odin-flex odin-flex-col odin-items-center odin-justify-center',
              'odin-border-2 odin-rounded-xl',
              colorScheme.letterBorder,
              colorScheme.letterBackground,
            )}
          >
            <span
              className={cn(
                'odin-text-8xl odin-font-[450] odin-leading-none',
                colorScheme.letterColor,
              )}
            >
              {copyScheme.badgeTypeLetter}
            </span>
            <span
              className={cn('odin-text-md -odin-mt-3', colorScheme.letterColor)}
            >
              {copyScheme.badgeTypeName}
            </span>
          </div>

          <div className="odin-flex odin-flex-col odin-items-center odin-gap-1">
            <div className="odin-text-lg odin-font-[450] odin-text-center odin-text-gray-900">
              {badgeName}
            </div>
            <div className="odin-text-md odin-text-center odin-text-gray-900">
              {jobsiteName}
            </div>
          </div>

          <QRCode value={getCurrentDomain()} size={100} />

          <div
            className={cn(
              'odin-w-full odin-text-white',
              'odin-text-center odin-text-sm odin-rounded-full odin-py-1',
              colorScheme.returnPromptColor,
            )}
          >
            Return badge when exiting
          </div>
        </div>
      </div>
    </div>
  );
}
